/**
 * AMP-2 pure core — plan + apply a batch of amendments across an SED rebuild.
 *
 * EXTRACTED from services/sedAmendments.ts (F1, 2026-07-02) so the SERVER
 * bootstrap job can reuse it: sedAmendments.ts imports client-only modules
 * (`authedFetch` → firebase web auth) at module scope, which must never enter
 * the Cloud Run job bundle. This module is PURE — types only, no I/O, no
 * client deps. services/sedAmendments re-exports both symbols, so every
 * existing client call site is untouched.
 *
 * WHY THIS EXISTS (D1/D2, Path-to-GO audit 2026-07-02): all SED rebuild paths
 * now route through the server bootstrap job, which rebuilt from scratch with
 * `amendments: []` — silently reverting every human resolution (live-proven:
 * Redhead acres/zoning/species amendments lost on the 2026-07-02 pre-flight
 * rebuild). The server job folds prior amendments into the fresh SED via this
 * planner before saving.
 */
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { SEDAmendment } from '../../types/sedAmendments';
import { sedValuesEqual } from './sedValueEquality';

/**
 * Per-amendment outcome of a reapply plan:
 *  - `reapplied` — value carried onto the new SED (or already there).
 *  - `conflict`  — provider value shifted to a THIRD value; needs a user
 *    decision, never auto-overwritten.
 *  - `orphaned`  — the amendment's path no longer resolves on the new SED.
 *  - `skipped`   — the amendment's status isn't `applied`.
 *  - `error`     — the apply step threw; caller decides whether to retry.
 */
export type AmendmentReapplyOutcome =
  | { kind: 'reapplied'; amendmentId: string; variablePath: string }
  | { kind: 'conflict'; amendmentId: string; variablePath: string; providerValueNow: unknown; userValue: unknown }
  | { kind: 'orphaned'; amendmentId: string; variablePath: string }
  | { kind: 'skipped'; amendmentId: string; variablePath: string; reason: string }
  | { kind: 'error'; amendmentId: string; variablePath: string; error: string };

/**
 * In-memory plan + apply for carrying a batch of amendments across an SED
 * rebuild. Loads the new SED once, walks each amendment in turn, and produces
 * an `outcomes[]` array describing what happened plus the updated SED
 * reference. The CALLER persists the updated SED.
 *
 * Pure-ish: deep-clones the input SED so the original reference is never
 * mutated. Does NOT touch Firestore or GCS.
 *
 * Conflicts and orphans STAY on the input amendment list — they need a user
 * decision and shouldn't be silently rewritten.
 */
export function planAmendmentReapply(
  sed: SiteEvidenceDocument,
  amendments: SEDAmendment[],
  nowIso: string,
): {
  next: SiteEvidenceDocument;
  outcomes: AmendmentReapplyOutcome[];
  reapplied: SEDAmendment[];
  conflicts: AmendmentReapplyOutcome[];
} {
  const next: SiteEvidenceDocument = JSON.parse(JSON.stringify(sed));
  if (!Array.isArray(next.amendments)) next.amendments = [];
  const outcomes: AmendmentReapplyOutcome[] = [];
  const reapplied: SEDAmendment[] = [];
  const conflicts: AmendmentReapplyOutcome[] = [];

  for (const amendment of amendments) {
    if (amendment.status !== 'applied') {
      outcomes.push({
        kind: 'skipped',
        amendmentId: amendment.id,
        variablePath: amendment.variablePath,
        reason: `status='${amendment.status}'`,
      });
      continue;
    }

    // Walk the path on the new SED. Missing path ⇒ orphan.
    const parts = amendment.variablePath.split('.');
    if (parts.length < 2) {
      outcomes.push({
        kind: 'orphaned',
        amendmentId: amendment.id,
        variablePath: amendment.variablePath,
      });
      continue;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let target: any = next;
    let resolved = true;
    for (let i = 0; i < parts.length - 1; i++) {
      if (target == null || typeof target !== 'object') {
        resolved = false;
        break;
      }
      target = target[parts[i]];
    }
    if (!resolved || target == null || typeof target !== 'object') {
      outcomes.push({
        kind: 'orphaned',
        amendmentId: amendment.id,
        variablePath: amendment.variablePath,
      });
      continue;
    }
    const leafKey = parts[parts.length - 1];
    // AMP-2 — leaf-key existence check distinguishes "path resolves but the
    // variable was dropped from the schema" (orphan) from "path resolves to a
    // primitive whose value differs from amendment.oldValue" (conflict).
    // Reading `target[leafKey]` would yield `undefined` for both, but `in`
    // discriminates them.
    if (!(leafKey in (target as Record<string, unknown>))) {
      outcomes.push({
        kind: 'orphaned',
        amendmentId: amendment.id,
        variablePath: amendment.variablePath,
      });
      continue;
    }
    const providerValueNow = target[leafKey];

    // Three-way comparison:
    //   1. providerValueNow === oldValue ⇒ provider unchanged; safe to
    //      overwrite with the user's amended value.
    //   2. providerValueNow === newValue ⇒ user's amendment already landed
    //      (idempotent reapply); just record the outcome.
    //   3. otherwise ⇒ provider has shifted. Don't auto-overwrite.
    // `sedValuesEqual`, not a local `===`: the persistence layer coerces
    // `undefined` -> `null` on every SED write, while a freshly compiled
    // section leaves an unreturned optional as a key present with `undefined`.
    // The previous local comparator short-circuited `a != null` and called
    // those two representations different, so an amendment to a field the
    // provider does not populate was classified as "provider shifted", the
    // human decision was dropped, and a conflict was raised naming a provider
    // value of `undefined`. Shared with the top-up replay so the rebuild and
    // refresh paths cannot disagree about what a user decision is worth.
    const eq = sedValuesEqual;

    if (eq(providerValueNow, amendment.newValue)) {
      // Already-applied no-op. Track it so the toast can still say
      // "N amendments preserved" rather than vanishing the count.
      outcomes.push({
        kind: 'reapplied',
        amendmentId: amendment.id,
        variablePath: amendment.variablePath,
      });
      reapplied.push(amendment);
      continue;
    }

    if (!eq(providerValueNow, amendment.oldValue)) {
      // Provider has shifted to a third value. Surface as a conflict.
      const outcome: AmendmentReapplyOutcome = {
        kind: 'conflict',
        amendmentId: amendment.id,
        variablePath: amendment.variablePath,
        providerValueNow,
        userValue: amendment.newValue,
      };
      outcomes.push(outcome);
      conflicts.push(outcome);
      continue;
    }

    // Safe to overwrite. Mirror the row in the in-band amendments[] audit
    // trail with a 'reapplied' marker; AMP-2's reappliedFromAmendmentId on
    // the Firestore record carries the back-pointer to the original id.
    target[leafKey] = amendment.newValue;
    next.amendments.push({
      field: amendment.variablePath,
      originalValue: amendment.oldValue as string | number | boolean | null,
      correctedValue: amendment.newValue as string | number | boolean | null,
      correctedBy: amendment.createdByEmail,
      reason: `${amendment.reason} (reapplied from sed ${amendment.sedId})`,
      source: amendment.source.label,
      timestamp: nowIso,
    });
    outcomes.push({
      kind: 'reapplied',
      amendmentId: amendment.id,
      variablePath: amendment.variablePath,
    });
    reapplied.push(amendment);
  }

  if (reapplied.length > 0) {
    next.version = (next.version ?? 1) + 1;
    next.collectedAt = nowIso;
  }

  return { next, outcomes, reapplied, conflicts };
}
